import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import openSocket from "socket.io-client";

import { openApi } from "../../services/api";
import useWebRTCCall from "../../hooks/useWebRTCCall";

const VERDE = "#1DB25A";

const wrap = {
  minHeight: "100vh",
  background: "#0b1510",
  color: "#eaf3ee",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
  boxSizing: "border-box",
};
const card = {
  width: "100%",
  maxWidth: 380,
  background: "#0f1d16",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 20,
  padding: 28,
  textAlign: "center",
};
const brand = { fontWeight: 800, letterSpacing: "-0.03em", fontSize: 18, marginBottom: 6 };
const bigBtn = (bg) => ({
  marginTop: 18,
  background: bg,
  color: bg === VERDE ? "#06110b" : "#fff",
  border: "none",
  borderRadius: 999,
  padding: "14px 22px",
  fontSize: 16,
  fontWeight: 700,
  cursor: "pointer",
  width: "100%",
});

const decodeJwt = (t) => {
  try {
    return JSON.parse(
      atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
  } catch (e) {
    return null;
  }
};

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const LigarPublico = () => {
  const { token } = useParams();
  const payload = useMemo(() => decodeJwt(token), [token]);
  const [turn, setTurn] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [micError, setMicError] = useState(false);
  const socketRef = useRef(null);

  const getSocket = useCallback(() => socketRef.current, []);

  const { state, muted, seconds, acceptCall, hangup, toggleMute, remoteAudioRef } = useWebRTCCall({
    getSocket,
    ticketId: payload?.ticketId,
    turn,
    role: "callee",
  });

  useEffect(() => {
    if (!payload || !payload.companyId || !payload.ticketId) {
      setInvalid(true);
      return undefined;
    }
    openApi
      .get("/call/turn-credentials", { params: { callToken: token } })
      .then(({ data }) => setTurn(data))
      .catch(() => setInvalid(true));

    const url = `${process.env.REACT_APP_BACKEND_URL}/${payload.companyId}`;
    const socket = openSocket(url, {
      query: { token: `Bearer ${token}` },
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;
    return () => {
      try { socket.disconnect(); } catch (e) {}
    };
  }, [payload, token]);

  const onAtender = async () => {
    try {
      setMicError(false);
      // ring:true avisa o painel de quem está na conversa (cliente inicia).
      await acceptCall({ ring: true });
    } catch (e) {
      setMicError(true);
    }
  };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={brand}>
          Zappro<span style={{ color: VERDE }}>.</span>
        </div>

        <audio ref={remoteAudioRef} autoPlay />

        {invalid && (
          <>
            <h2 style={{ fontSize: 20, margin: "18px 0 8px" }}>Link indisponível</h2>
            <p style={{ color: "rgba(234,243,238,.6)", fontSize: 14 }}>
              Este link de chamada expirou ou é inválido. Peça um novo ao atendente.
            </p>
          </>
        )}

        {!invalid && (state === "idle" || state === "connecting" || state === "calling") && (
          <>
            <div style={{ fontSize: 44, margin: "12px 0" }}>📞</div>
            <h2 style={{ fontSize: 19, margin: "6px 0" }}>Chamada de voz</h2>
            <p style={{ color: "rgba(234,243,238,.6)", fontSize: 14 }}>
              {state === "connecting" ? "Conectando…" : "Toque para falar com o atendente."}
            </p>
            {micError && (
              <p style={{ color: "#ff6b6b", fontSize: 13 }}>
                Precisamos da permissão do microfone para a chamada.
              </p>
            )}
            <button style={bigBtn(VERDE)} onClick={onAtender} disabled={!turn || state === "connecting"}>
              Ligar
            </button>
          </>
        )}

        {!invalid && state === "in-call" && (
          <>
            <div style={{ fontSize: 44, margin: "12px 0" }}>🔊</div>
            <h2 style={{ fontSize: 19, margin: "6px 0" }}>Em chamada</h2>
            <p style={{ color: VERDE, fontSize: 15, fontWeight: 600 }}>{fmt(seconds)}</p>
            <button style={bigBtn("#25402f")} onClick={toggleMute}>
              {muted ? "Desmutar" : "Mutar"}
            </button>
            <button style={bigBtn("#c0392b")} onClick={hangup}>
              Encerrar
            </button>
          </>
        )}

        {!invalid && (state === "ended" || state === "failed") && (
          <>
            <div style={{ fontSize: 44, margin: "12px 0" }}>{state === "failed" ? "⚠️" : "✅"}</div>
            <h2 style={{ fontSize: 19, margin: "6px 0" }}>
              {state === "failed" ? "Falha na chamada" : "Chamada encerrada"}
            </h2>
            <p style={{ color: "rgba(234,243,238,.6)", fontSize: 14 }}>Você pode fechar esta página.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default LigarPublico;
